import IndividualContactData from "../../../../model/contact/individual/IndividualContactData";
import {FormField, OptionalFormField, RequiredFormField} from "../../../../validation/FormField";
import * as moment from "moment";
import FormValidation from "../../../../validation/FormValidation";
import IndividualContactMainData from "../../../../model/contact/individual/IndividualContactMainData";
import IndividualCommunication from "../../../../model/contact/individual/IndividualCommunication";
import FormValidators from "../../../../validation/FormValidators";
import {none, Option, option} from "ts-option";
import {ValidationResult} from "../../../../validation/ValidationResult";
import Address from "../../../../model/address/Address";
import BaseFormController from "../../../../validation/BaseFormController";
import IndividualContact from "../../../../model/contact/IndividualContact";
import Phone from "../../../../model/phone/Phone";
import Email from "../../../../model/email/Email";

const uuidv4 = require("uuid/v4");

/**
 * Contact form data.
 */
export default class ContactForm extends BaseFormController<ContactForm> {
	constructor(formId: string,
		public readonly primaryAddress: OptionalFormField<Address>,
		public readonly title: OptionalFormField<string>,
		public readonly firstName: RequiredFormField<string>,
		public readonly lastName: RequiredFormField<string>,
		public readonly birthDate: OptionalFormField<moment.Moment>,
		public readonly emails: OptionalFormField<Email[]>,
		public readonly phoneNumbers: OptionalFormField<Phone[]>,
		public readonly communicationConsent: OptionalFormField<boolean>,
		public readonly servicePlanRemindersConsent: OptionalFormField<boolean>,
		public readonly creationDateTime: OptionalFormField<moment.Moment>,
		public readonly brandId: RequiredFormField<string>,
		public readonly partnerBrandId: OptionalFormField<string>,
		public readonly archived: RequiredFormField<boolean>) {
		super(lastName.value.isEmpty ? firstName : lastName, formId);
	}

	static createContactForm({
		primaryAddress = none,
		title = none,
		firstName = none,
		lastName = none,
		birthDate = none,
		emails = none,
		phoneNumbers = none,
		communicationConsent = none,
		servicePlanRemindersConsent = none,
		creationDateTime = none,
		brandId = none,
		partnerBrandId = none,
		archived = none
	}) {
		const phonesField = OptionalFormField.create<Phone[]>(phoneNumbers);
		return new ContactForm(
			uuidv4(),
			OptionalFormField.create<Address>(primaryAddress),
			OptionalFormField.create<string>(title),
			RequiredFormField.create<string>(firstName, "First name is required", [
				FormValidators.nonEmptyString("First name is required")
			]),
			RequiredFormField.create<string>(lastName, "Last name is required", [
				FormValidators.nonEmptyString("Last name is required")
			]),
			OptionalFormField.create<moment.Moment>(birthDate, [FormValidators.dateInPast]),
			OptionalFormField.create<Email[]>(emails, [
				FormValidators.atLeasOneRequired("At least one email or phone number is required", [phonesField.id]),
				FormValidators.emails
			]),
			phonesField,
			OptionalFormField.create<boolean>(communicationConsent),
			OptionalFormField.create<boolean>(servicePlanRemindersConsent),
			OptionalFormField.create<moment.Moment>(creationDateTime),
			RequiredFormField.create<string>(brandId, "Brand is required", [
				FormValidators.nonEmptyString("Brand is required")
			]),
			OptionalFormField.create<string>(partnerBrandId),
			RequiredFormField.create<boolean>(archived)
		)
	}

	static createFromContact(contact: IndividualContact, contactAddress: Option<Address> = none) {
		const mainData = contact.data.mainData;
		const communication = contact.data.communication;
		return ContactForm.createContactForm({
			primaryAddress: contactAddress,
			title: mainData.title,
			firstName: option(mainData.firstName),
			lastName: option(mainData.lastName),
			birthDate: mainData.dateOfBirth,
			emails: communication.emails,
			phoneNumbers: communication.phoneNumbers,
			communicationConsent: contact.data.communicationConsent,
			servicePlanRemindersConsent: contact.data.servicePlanRemindersConsent,
			creationDateTime: option(contact.data.creationDateTime),
			brandId: option(contact.data.brandId),
			partnerBrandId: contact.data.partnerBrandId,
			archived: option(contact.data.archived)
		});
	}

	validate(): ContactForm {
		return FormValidation.validateForm<ContactForm>(this, (form, config) => form.copy(config));
	}

	onIndividualValid(siteId: Option<string>,
		onValid: (contact: IndividualContactData) => void,
		onInvalid: () => void): ValidationResult<IndividualContactData> {
		return FormValidation.validateFields(
			[this.primaryAddress, this.title, this.firstName, this.lastName, this.birthDate, this.emails, this.phoneNumbers,
				this.brandId, this.partnerBrandId, this.archived],
			() => {
				const main = new IndividualContactMainData(
					this.title.getValue(),
					this.firstName.getValue().trim(),
					this.lastName.getValue().trim(),
					this.birthDate.getValue()
				);
				const communication = new IndividualCommunication(this.emails.getOptionalValue(),
					this.phoneNumbers.getOptionalValue());
				const brandId = this.brandId.getValue();
				const partnerBrandId = this.partnerBrandId.getOptionalValue();
				const archived = this.archived.getValue();
				return new IndividualContactData(main, communication, siteId, none, none, null, brandId, partnerBrandId,
					archived);
			}).onSuccess(contact => onValid(contact)).onFailure(() => onInvalid());
	}

	copy({
		primaryAddress = this.primaryAddress,
		title = this.title,
		firstName = this.firstName,
		lastName = this.lastName,
		birthDate = this.birthDate,
		emails = this.emails,
		phoneNumbers = this.phoneNumbers,
		communicationConsent = this.communicationConsent,
		servicePlanRemindersConsent = this.servicePlanRemindersConsent,
		creationDateTime = this.creationDateTime,
		brandId = this.brandId,
		partnerBrandId = this.partnerBrandId,
		archived = this.archived
	}): ContactForm {
		return new ContactForm(this.formId, primaryAddress, title, firstName, lastName, birthDate, emails, phoneNumbers,
			communicationConsent, servicePlanRemindersConsent, creationDateTime, brandId, partnerBrandId, archived)
	}

	fields(): FormField<any> [] {
		return [];
	}
}